/**
 * Old-UI navigation tree.
 *
 * Reuses the section → sub-section STRUCTURE from the new nav
 * (`src/components/nav-v4/config.ts`) — same sections, children, groupings
 * (`separatorBefore`), role gating (`deniedRoles`) and venue-context — but
 * REMAPS every destination to the routes the OLD UI actually serves. The new
 * nav points at v3-era routes (e.g. `bookings/day-planner-3`); the old UI uses
 * the `-fast` / legacy equivalents. The rewrite is URL-level so it catches every
 * occurrence across the tree.
 *
 * This file is data only — the reworked `AnimatedSidebar` + old top bar + old
 * mobile nav consume it with the shared pure helpers (`isNavItemVisible`,
 * `resolveLabel`, `resolveHref`, `findItemChainForPath`).
 */
import {
  Archive,
  AtSign,
  Download,
  BarChart3,
  Bell,
  Boxes,
  Building2,
  ChartSpline,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Eye,
  FileClock,
  Gift,
  Globe,
  History,
  Home,
  Inbox,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Link2,
  Mail,
  MessageSquare,
  Monitor,
  Paintbrush,
  PlayCircle,
  Plus,
  QrCode,
  Settings,
  Sparkles,
  Tag,
  Ticket,
  Upload,
  User,
  Users,
  Zap,
  FileSpreadsheet,
} from 'lucide-react'
import { SquareSvgIcon } from '@/ux/icons/square'
import {
  orgNavPrimary,
  orgNavSecondary,
  resolveHref,
  type NavItem,
  type NavContext,
} from '@/components/nav-v4/config'
import { WIFI_HARDWARE_GROUPS } from '@/components/nav-v4/wifi-hardware-groups'
import { isPaidVenue } from '@/utils/venue-access'
import config from '@/config'

/**
 * Loyalty segments that sit where a scheme id would but are NOT one: the
 * scheme-less service routes (which render the "pick a scheme" gate), plus
 * the org-level pages. Without this list `/orgId/loyalty/customers` would
 * parse `customers` as the selected scheme and every gated href would build
 * `/loyalty/customers/customers`.
 */
const NON_SCHEME_LOYALTY_SEGMENTS = new Set([
  'reports',
  'new',
  'overview',
  'customers',
  'history',
  'rewards',
  'vouchers',
  'links',
  'gift-cards',
  'member-imports',
  'preview-scheme',
  'preview-rewards',
])

/**
 * Pull the loyalty scheme id out of a path, for either
 * `/:orgId/loyalty/:schemeId/...` or
 * `/:orgId/venues/:serial/loyalty/:schemeId/...`. Returns undefined when no
 * scheme is selected (the schemes list, reports, new, or a gate route).
 */
export function parseSchemeId(pathname: string): string | undefined {
  const segs = pathname.split('/').filter(Boolean)
  const i = segs.indexOf('loyalty')
  if (i < 0) return undefined
  const next = segs[i + 1]
  if (!next || NON_SCHEME_LOYALTY_SEGMENTS.has(next)) return undefined
  return next
}

/** Rewrite a single new-UI href to its old-UI equivalent. */
function oldifyHref(href: string): string {
  return href
    .replace('/bookings/day-planner-3', '/bookings/day-planner-fast')
    .replace('/bookings/floorplan-3-edit', '/bookings/floor-plans-edit')
    .replace('/bookings/floorplan-3', '/bookings/floorplan-fast')
    .replace('/inbox/threads', '/inbox')
    .replace('/home2025', '')
}

/**
 * Items that have no equivalent surface in the old UI — dropped from the old
 * nav rather than linking to a dead route.
 *
 * `knowledge-base` is a deliberate, temporary exception to that rule: the
 * agent page DOES exist at /{orgId}/knowledge-base, but both Knowledge Base
 * rows are being taken off the old sidebar while the surface is reworked
 * around the support agent (Josef, 8 Sep 2026). The route and its page are
 * untouched, so restoring the row is deleting one line here.
 */
const OMIT_IDS = new Set<string>(['knowledge-base'])

function toOldNavItem(item: NavItem): NavItem {
  const to = item.to
  const venueTo = item.venueTo
  const children = item.children
    ?.filter((child) => !OMIT_IDS.has(child.id))
    .map(toOldNavItem)
  return {
    ...item,
    to: to ? (ctx) => oldifyHref(to(ctx)) : undefined,
    venueTo: venueTo ? (ctx) => oldifyHref(venueTo(ctx)) : undefined,
    children,
  }
}

/**
 * Bookings settings pages the legacy inner rail used to host ("Other" +
 * "Archived") that have no entry in the new nav tree. The old UI no longer
 * renders that inner rail (the sidebar drill-in replaces it), so these are
 * folded into the Bookings drill-in instead. Venue-scoped — they only existed
 * per-venue under `/venues/:serial/bookings/...`.
 */
const EXTRA_BOOKINGS_SETTINGS_BASE: NavItem[] = [
  // NB: the Landing Page surface (old "Design") flows through from the
  // new nav config (`bookings-landing-page`) — no extra entry needed.
  {
    id: 'bookings-email-templates',
    label: 'Email Templates',
    icon: Mail,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/settings/email-templates`,
  },
  // NB: Payment Reminders deliberately has no top-level item — it lives in
  // the new nav's Main Settings sub-nav (after Payments) only.
  {
    id: 'bookings-google',
    label: 'Google Integration',
    icon: Globe,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/settings/google`,
  },
  {
    id: 'bookings-tracking',
    label: 'Tracking',
    icon: BarChart3,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/settings/tracking`,
  },
  {
    id: 'bookings-link-builder',
    label: 'Link Builder',
    icon: Link2,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/settings/embed-links`,
  },
  {
    id: 'bookings-csv-imports',
    label: 'CSV Imports',
    icon: Upload,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/csv-imports`,
    separatorBefore: true,
    // Staff-only — the zion CSV import endpoints are @stampede.ai-gated.
    isVisible: ({ userEmail }) =>
      (userEmail ?? '').toLowerCase().endsWith('@stampede.ai'),
  },
  {
    id: 'bookings-live-migration',
    label: 'Live Migration',
    icon: Monitor,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/live-migration`,
    // Staff-only, same gate as CSV Imports — this drives a live agent-smith
    // browser session, not just a page.
    isVisible: ({ userEmail }) =>
      (userEmail ?? '').toLowerCase().endsWith('@stampede.ai'),
  },
  {
    id: 'bookings-csv-exports',
    label: 'CSV Exports',
    icon: Download,
    requiresVenue: true,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/csv-exports`,
  },
  {
    // Single red parent; its archived options read normally (not red) inside.
    id: 'bookings-archived',
    label: 'Archived',
    icon: Archive,
    tone: 'destructive',
    requiresVenue: true,
    separatorBefore: true,
    // Parent lands on the first archived section; the accordion expands here.
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/bookings/settings/tables/areas/archived`,
    children: [
      {
        id: 'bookings-archived-areas',
        label: 'Areas',
        icon: Archive,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/tables/areas/archived`,
      },
      {
        id: 'bookings-archived-tables',
        label: 'Tables',
        icon: Archive,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/tables/archived`,
      },
      {
        id: 'bookings-archived-combinations',
        label: 'Combinations',
        icon: Archive,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/tables/combinations/archived`,
      },
      {
        id: 'bookings-archived-booking-types',
        label: 'Booking Types',
        icon: Archive,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/booking-types/archived`,
      },
      {
        id: 'bookings-archived-special-dates',
        label: 'Special Dates',
        icon: Archive,
        requiresVenue: true,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/bookings/settings/special-dates/archived`,
      },
    ],
  },
]

/**
 * Always show every Bookings settings item, even with no venue selected — drop
 * `requiresVenue` (which hid them) and give each an org-level `to` that lands on
 * the venue picker (Square pattern), continuing to its venue-scoped page once a
 * venue is chosen. `isVisible`/`separatorBefore` etc. are preserved.
 */
const EXTRA_BOOKINGS_SETTINGS: NavItem[] = EXTRA_BOOKINGS_SETTINGS_BASE.map(
  (item) => {
    if (!item.venueTo) return item
    const sub = item
      .venueTo({ orgId: '__O', serial: '__S' } as NavContext & {
        serial: string
      })
      .replace('/__O/venues/__S/', '')
    return {
      ...item,
      requiresVenue: false,
      to: ({ orgId }) =>
        `/${orgId}/bookings/choose-venue?next=${encodeURIComponent(sub)}&label=${encodeURIComponent(String(item.label))}`,
    }
  }
)

/**
 * Append the legacy settings pages onto the Bookings section.
 *
 * The standard children are shown regardless of venue context: with no venue
 * selected, items that have an org-level page (overview, enquiries, deposits,
 * pre-orders, reports) resolve to it, and venue-only items resolve to their
 * org route which renders the "choose a venue" gate. So the full menu stays
 * visible and venue-only entries prompt for a venue when clicked.
 */
function withExtraBookingsSettings(item: NavItem): NavItem {
  if (item.id !== 'bookings') return item

  const byId = (id: string) => EXTRA_BOOKINGS_SETTINGS.find((e) => e.id === id)
  const withSep = (id: string, sep: boolean): NavItem | null => {
    const it = byId(id)
    return it ? { ...it, separatorBefore: sep } : null
  }

  // Settings spliced in next to specific standard items: the Landing Page +
  // Email Templates section sits after Events (Landing Page is the new-nav
  // item, relocated here to keep the old "Design + Email Templates"
  // grouping); CSV uploader sits with the layout group, right after
  // Combinations.
  const landingPage = (item.children ?? []).find(
    (c) => c.id === 'bookings-landing-page'
  )
  // Reporting is relocated out of the Deposits/Outstanding/Booking Alerts (Ops)
  // group into its own "CSV Imports + Reporting" section, placed under the
  // Google Integration / Tracking / Link Builder group (see `rest` below).
  const reporting = (item.children ?? []).find(
    (c) => c.id === 'bookings-reporting'
  )
  const afterEvents = [
    ...(landingPage ? [{ ...landingPage, separatorBefore: true }] : []),
    withSep('bookings-email-templates', false),
  ].filter(Boolean) as NavItem[]
  // CSV Imports is no longer spliced next to the layout group — it now leads
  // the "CSV Imports + Reporting" section down in `rest`.
  const afterCombinations: NavItem[] = []

  const placed = new Set(
    [...afterEvents, ...afterCombinations].map((e) => e.id)
  )
  // Everything else (Google, Tracking, Link builder, Archived…) appended after,
  // led by a separator so it reads as its own group.
  const rest = EXTRA_BOOKINGS_SETTINGS.filter((e) => !placed.has(e.id)).map(
    (e, i) => (i === 0 ? { ...e, separatorBefore: true } : e)
  )
  // Drop Reporting into the CSV section — right after CSV Exports, with no
  // separator, so CSV Imports + CSV Exports + Reporting read as one section
  // beneath Link Builder.
  if (reporting) {
    const csvIdx = rest.findIndex((e) => e.id === 'bookings-csv-exports')
    const at = csvIdx === -1 ? rest.length : csvIdx + 1
    rest.splice(at, 0, { ...reporting, separatorBefore: false })
  }

  // Floorplan Editor is relocated out of its default spot (next to Floorplan)
  // to sit directly under the CSV uploader, per product preference.
  const floorplanEdit = (item.children ?? []).find(
    (c) => c.id === 'bookings-floorplan-edit'
  )

  const children: NavItem[] = []
  for (const child of item.children ?? []) {
    if (child.id === 'bookings-floorplan-edit') continue
    // Relocated into the Email Templates section (afterEvents) above.
    if (child.id === 'bookings-landing-page') continue
    // Relocated into the CSV Imports + Reporting section (rest) below.
    if (child.id === 'bookings-reporting') continue
    // List View stays in the OLD-UI mobile menu for customers. The new nav
    // hides it on mobile for non-staff (their target, list-view-fast, is
    // desktop-oriented), but old-UI customers still need a way to reach it
    // on a phone, so the staff-only mobile gate is lifted here.
    if (child.id === 'bookings-list') {
      children.push({ ...child, hideOnMobile: false })
      continue
    }
    // Smart Seating flows through from the new nav as venue-only. Match its
    // neighbours (CSV Imports etc.): stay visible at org scope and resolve to
    // the choose-venue gate instead of vanishing.
    if (child.id === 'bookings-optimisation') {
      children.push({
        ...child,
        requiresVenue: false,
        to: ({ orgId }) =>
          `/${orgId}/bookings/choose-venue?next=${encodeURIComponent(
            'bookings/optimisation'
          )}&label=${encodeURIComponent('Smart Seating')}`,
      })
      continue
    }
    children.push(child)
    if (child.id === 'bookings-events') children.push(...afterEvents)
    if (child.id === 'bookings-combinations') {
      children.push(...afterCombinations)
      if (floorplanEdit) {
        children.push({ ...floorplanEdit, separatorBefore: false })
      }
    }
  }
  children.push(...rest)

  return { ...item, children }
}

/**
 * Wi-Fi drill-in mirrors the venue Wi-Fi overview cards. Every item is always
 * shown; venue-only surfaces resolve (no venue) to an org route that renders
 * the "choose a venue" gate, and (with a venue) to the venue page — same
 * show-all + venue-picker pattern used across the app.
 */
const WIFI_CHILDREN: NavItem[] = [
  {
    id: 'wifi-settings',
    label: 'Captive Portal Settings',
    icon: Settings,
    to: ({ orgId }) => `/${orgId}/wi-fi/settings`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/settings`,
  },
  {
    id: 'wifi-surveys',
    label: 'Captive Portal Surveys',
    icon: ClipboardList,
    // Venue-only: at org scope land on the venue-picker gate instead of the
    // auto-pick-first-venue page.
    to: ({ orgId }) =>
      `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
        'wi-fi/survey'
      )}&label=${encodeURIComponent('Captive Portal Surveys')}`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/survey`,
    // Surveys run on the free captive-portal journey — not valid for a paid
    // network, which shows Portal Payments instead.
    isVisible: (ctx) => !isPaidVenue(ctx.venue),
  },
  {
    id: 'wifi-upsell',
    label: 'Captive Portal Upsell',
    icon: Zap,
    to: ({ orgId }) =>
      `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
        'wi-fi/upsell'
      )}&label=${encodeURIComponent('Captive Portal Upsell')}`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/upsell`,
  },
  {
    id: 'wifi-payments',
    label: 'Portal Payments',
    icon: CreditCard,
    // Paid networks only — the venue's trinity Wi-Fi config carries the type,
    // so this is hidden at org scope and on free venues.
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/payments`,
    requiresVenue: true,
    isVisible: (ctx) => isPaidVenue(ctx.venue),
  },
  {
    id: 'wifi-branding',
    label: 'Captive Portal Branding',
    icon: Paintbrush,
    // No "all venues" portal view — gate to the venue picker, then land on
    // the venue-scoped page.
    to: ({ orgId }) =>
      `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
        'brand/wi-fi'
      )}&label=${encodeURIComponent('Captive Portal Branding')}`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/brand/wi-fi`,
    activeMatch: 'prefix',
  },
  {
    id: 'wifi-insights',
    label: 'Captive Insights',
    icon: BarChart3,
    separatorBefore: true,
    // Venue-only: at org scope land on the venue-picker gate, then continue
    // to the per-venue insight page.
    to: ({ orgId }) =>
      `/${orgId}/wi-fi/choose-venue?next=${encodeURIComponent(
        'wi-fi/insight'
      )}&label=${encodeURIComponent('Captive Insights')}`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/wi-fi/insight`,
  },
  // ── Hardware ──────────────────────────────────────────────────────────
  // Below the separator a venue sees exactly ONE hardware group (SnapGuard or
  // Hardware), chosen by its `hardwareTier`. Shared with the new-UI nav via
  // WIFI_HARDWARE_GROUPS so the two trees never diverge.
  ...WIFI_HARDWARE_GROUPS,
]

/** Replace the Wi-Fi children with the venue overview-card set. */
function withWifiChildren(item: NavItem): NavItem {
  if (item.id !== 'wi-fi') return item
  return { ...item, children: WIFI_CHILDREN }
}

/**
 * Gifting drill-in. Gift cards are attributed to a venue (via `card.serial`),
 * so the Gift cards item filters to the selected venue with `?serial=` when one
 * is active; the rest are org-level surfaces.
 */
const GIFTING_CHILDREN: NavItem[] = [
  {
    id: 'gifting-cards',
    label: 'Gift Cards',
    icon: Gift,
    to: ({ orgId }) => `/${orgId}/gifting/cards`,
    venueTo: ({ orgId, serial }) => `/${orgId}/gifting/cards?serial=${serial}`,
  },
  {
    id: 'gifting-activations',
    label: 'Activations',
    icon: Ticket,
    to: ({ orgId }) => `/${orgId}/gifting/activations`,
  },
  {
    id: 'gifting-reports',
    label: 'Reports',
    icon: BarChart3,
    to: ({ orgId }) => `/${orgId}/gifting/reports`,
  },
  {
    id: 'gifting-scan',
    label: 'Scan',
    icon: QrCode,
    to: ({ orgId }) => `/${orgId}/gifting/scan`,
  },
  // "Add Card" used to live here as a sub-menu row. It's an ACTION, not a
  // destination, so it now sits in the Gift cards page toolbar beside the date
  // range — where the thing it creates actually appears. The `?create=1` deep
  // link it used still works.
]

/** Give Gifting a drill-in sub-menu. */
function withGiftingChildren(item: NavItem): NavItem {
  if (item.id !== 'gifting') return item
  return { ...item, children: GIFTING_CHILDREN }
}

/**
 * Reviews drill-in mirrors the venue Reviews overview cards. Show-all +
 * venue-picker: items with a real org page resolve there (no venue); the
 * venue-only ones (e.g. Page settings) gate to the venue picker.
 */
const REVIEWS_CHILDREN: NavItem[] = [
  {
    id: 'reviews-responses',
    label: 'Responses',
    icon: MessageSquare,
    to: ({ orgId }) => `/${orgId}/reviews/responses`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reviews/responses`,
  },
  {
    id: 'reviews-page-settings',
    label: 'Page Settings',
    icon: Settings,
    to: ({ orgId }) => `/${orgId}/reviews/settings`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reviews/settings`,
  },
  {
    id: 'reviews-google',
    label: 'Google',
    icon: Globe,
    to: ({ orgId }) => `/${orgId}/reviews/google`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/reviews/google`,
  },
  {
    id: 'reviews-reports',
    label: 'Reports',
    icon: BarChart3,
    // Parent lands on the first child; the accordion expands.
    to: ({ orgId }) => `/${orgId}/reviews/reports`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reviews/reports`,
    children: [
      {
        id: 'reviews-reports-overview',
        label: 'Overview',
        icon: BarChart3,
        activeMatch: 'exact',
        to: ({ orgId }) => `/${orgId}/reviews/reports`,
        // In the venue context this is that venue's own report.
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/reports`,
      },
      {
        id: 'reviews-reports-metrics',
        label: 'Metrics',
        icon: BarChart3,
        to: ({ orgId }) => `/${orgId}/reviews/reports/metrics`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/reports/metrics`,
      },
    ],
  },
  {
    id: 'reviews-competitors',
    label: 'Competitor Analysis',
    icon: ChartSpline,
    to: ({ orgId }) => `/${orgId}/reviews/competitors`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reviews/competitors`,
  },
  {
    id: 'reviews-ai-settings',
    label: 'AI Settings',
    icon: Sparkles,
    // Parent lands on the first child; accordion auto-expands here.
    to: ({ orgId }) => `/${orgId}/reviews/ai-settings/auto-generation`,
    venueTo: ({ orgId, serial }) =>
      `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
    children: [
      {
        id: 'reviews-ai-auto-generation',
        label: 'Auto-Generation Rules',
        icon: Sparkles,
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/auto-generation`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/auto-generation`,
      },
      {
        id: 'reviews-ai-approval-queue',
        label: 'Approval Queue',
        icon: Inbox,
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/approval-queue`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/approval-queue`,
      },
      {
        id: 'reviews-ai-response-prompts',
        label: 'Response Prompts',
        icon: MessageSquare,
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/response-prompts`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/response-prompts`,
      },
      {
        id: 'reviews-ai-activity-log',
        label: 'Activity Log',
        icon: FileClock,
        // Staff-only — mirrors the `reviewsActivityLog` feature flag.
        isVisible: ({ userEmail }) =>
          (userEmail ?? '').toLowerCase().endsWith('@stampede.ai'),
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/activity-log`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/activity-log`,
      },
      {
        id: 'reviews-ai-analytics',
        label: 'Analytics',
        icon: BarChart3,
        to: ({ orgId }) => `/${orgId}/reviews/ai-settings/analytics`,
        venueTo: ({ orgId, serial }) =>
          `/${orgId}/venues/${serial}/reviews/ai-settings/analytics`,
      },
    ],
  },
  {
    // Call-to-action, last sub-menu item. An `action` (not a link): clicking it
    // opens the globally-mounted create-review-page dialog over the current
    // page — no route change. `to` is a graceful fallback only.
    id: 'reviews-add-page',
    label: 'Add Review Page',
    icon: Plus,
    separatorBefore: true,
    activeMatch: 'exact',
    action: 'create-review-page',
    to: ({ orgId }) => `/${orgId}/reviews`,
  },
]

/** Replace the Reviews children with the overview-card set. */
function withReviewsChildren(item: NavItem): NavItem {
  if (item.id !== 'reviews') return item
  return { ...item, children: REVIEWS_CHILDREN }
}

/**
 * Loyalty drill-in. Loyalty is scheme-based: "Schemes" lists every scheme with
 * no venue selected, or that venue's schemes when one is active (and always has
 * a create-scheme action). Individual scheme services (rewards, vouchers, gift
 * cards, customers…) live on the scheme page itself.
 */
/**
 * Loyalty list path (the schemes picker) — where scheme-scoped services send
 * you when no scheme is selected yet ("pick a scheme").
 */
const loyaltyListHref = (ctx: NavContext) =>
  ctx.serial
    ? `/${ctx.orgId}/venues/${ctx.serial}/loyalty`
    : `/${ctx.orgId}/loyalty`

/**
 * The scheme-less path for a service — the "pick a scheme to continue" gate,
 * which then forwards to this same service under the chosen scheme. Mirrors
 * how venue-only pages gate on a venue instead of dumping the user back on a
 * list and making them find their way again.
 */
const schemeGateHref = (service: string) => (ctx: NavContext) =>
  ctx.serial
    ? `/${ctx.orgId}/venues/${ctx.serial}/loyalty/${service}`
    : `/${ctx.orgId}/loyalty/${service}`

/** Build a scheme-service href, gated on a scheme being selected. */
const schemeServiceHref =
  (service: string) =>
  (ctx: NavContext): string => {
    if (!ctx.schemeId) return schemeGateHref(service)(ctx)
    return ctx.serial
      ? `/${ctx.orgId}/venues/${ctx.serial}/loyalty/${ctx.schemeId}/${service}`
      : `/${ctx.orgId}/loyalty/${ctx.schemeId}/${service}`
  }

/** The selected scheme's root (overview) page, gated on a scheme. */
const schemeRootHref = (ctx: NavContext): string => {
  if (!ctx.schemeId) return schemeGateHref('overview')(ctx)
  return ctx.serial
    ? `/${ctx.orgId}/venues/${ctx.serial}/loyalty/${ctx.schemeId}`
    : `/${ctx.orgId}/loyalty/${ctx.schemeId}`
}

const LOYALTY_CHILDREN: NavItem[] = [
  {
    id: 'loyalty-schemes',
    label: 'Schemes',
    icon: LayoutGrid,
    to: ({ orgId }) => `/${orgId}/loyalty`,
    venueTo: ({ orgId, serial }) => `/${orgId}/venues/${serial}/loyalty`,
    activeMatch: 'exact',
  },
  {
    id: 'loyalty-overview',
    label: 'Overview',
    icon: LayoutDashboard,
    requiresScheme: true,
    activeMatch: 'exact',
    to: schemeRootHref,
  },
  {
    id: 'loyalty-customers',
    label: 'Enrolled Guests',
    icon: Users,
    separatorBefore: true,
    requiresScheme: true,
    to: schemeServiceHref('customers'),
  },
  {
    id: 'loyalty-history',
    label: 'Interaction History',
    icon: FileClock,
    requiresScheme: true,
    to: schemeServiceHref('history'),
  },
  {
    id: 'loyalty-rewards',
    label: 'Rewards',
    icon: Gift,
    requiresScheme: true,
    to: schemeServiceHref('rewards'),
  },
  {
    id: 'loyalty-vouchers',
    label: 'Reward Vouchers',
    icon: Ticket,
    requiresScheme: true,
    to: schemeServiceHref('vouchers'),
  },
  {
    id: 'loyalty-links',
    label: 'Link Builder',
    icon: Link2,
    requiresScheme: true,
    to: schemeServiceHref('links'),
  },
  {
    id: 'loyalty-gift-cards',
    label: 'Square Gift Cards',
    icon: SquareSvgIcon,
    requiresScheme: true,
    to: schemeServiceHref('gift-cards'),
  },
  {
    id: 'loyalty-member-imports',
    label: 'Import Members',
    icon: FileSpreadsheet,
    requiresScheme: true,
    to: schemeServiceHref('member-imports'),
  },
  {
    // Stampede-only backfill of points-for-spend after a Square outage. The
    // route carries the same capability. Hidden until a scheme is picked: it
    // has no scheme-less gate route to fall back to.
    id: 'loyalty-points-catchup',
    label: 'Points catch-up',
    icon: History,
    capability: 'loyalty.points-catchup',
  
    requiresScheme: true,
    isVisible: (ctx) => Boolean(ctx.schemeId),
    to: schemeServiceHref('points-catchup'),
  },
  // In-app previews, not external links: the microsite renders in a phone
  // frame inside connect (it used to be an iframe bolted onto the Link
  // Builder). "Open in new tab" lives on the preview page itself.
  {
    id: 'loyalty-preview-scheme',
    label: 'Preview Scheme',
    icon: Eye,
    separatorBefore: true,
    requiresScheme: true,
    to: schemeServiceHref('preview-scheme'),
  },
  {
    id: 'loyalty-preview-rewards',
    label: 'Preview Rewards',
    icon: Gift,
    requiresScheme: true,
    to: schemeServiceHref('preview-rewards'),
  },
  {
    id: 'loyalty-scan',
    label: 'Scan',
    icon: QrCode,
    requiresScheme: true,
    // An action (opens the scanner), not a destination — its `?scan` href never
    // equals the query-less pathname, so with exact matching it's never active.
    activeMatch: 'exact',
    to: (ctx) =>
      ctx.schemeId ? `${schemeRootHref(ctx)}?scan=1` : loyaltyListHref(ctx),
  },
  {
    id: 'loyalty-reports',
    label: 'Reports',
    icon: BarChart3,
    separatorBefore: true,
    // Org-level reports page, pre-filtered to the picked scheme when there is
    // one (no separate scheme-reports route — the page filters by `?scheme=`).
    to: (ctx) =>
      ctx.schemeId
        ? `/${ctx.orgId}/loyalty/reports?scheme=${ctx.schemeId}`
        : `/${ctx.orgId}/loyalty/reports`,
  },
]

/** Give Loyalty a proper drill-in (Overview + Reports). */
function withLoyaltyChildren(item: NavItem): NavItem {
  if (item.id !== 'loyalty') return item
  return { ...item, children: LOYALTY_CHILDREN }
}

/** Primary ("Manage") group — old routes. */
export const oldOrgNavPrimary: NavItem[] = orgNavPrimary
  .filter((item) => !OMIT_IDS.has(item.id))
  .map(toOldNavItem)
  .map(withExtraBookingsSettings)
  .map(withWifiChildren)
  .map(withGiftingChildren)
  .map(withReviewsChildren)
  .map(withLoyaltyChildren)

/**
 * Marketplace is a drill-in section: Apps first, then API keys & webhooks.
 * (The route renders these directly — no inner tabbed-nav layout.)
 */
const MARKETPLACE_CHILDREN: NavItem[] = [
  {
    id: 'marketplace-apps',
    label: 'Apps',
    icon: LayoutGrid,
    to: ({ orgId }) => `/${orgId}/marketplace/apps`,
  },
  {
    id: 'marketplace-api-keys',
    label: 'API Keys & Webhooks',
    icon: KeyRound,
    to: ({ orgId }) => `/${orgId}/marketplace/api-keys`,
  },
  {
    id: 'marketplace-docs',
    label: 'Documentation',
    icon: ClipboardCheck,
    to: () => 'https://stampede.ai/developer',
    external: true,
  },
]

function withMarketplaceChildren(item: NavItem): NavItem {
  if (item.id !== 'marketplace') return item
  return { ...item, label: 'Marketplace & API', children: MARKETPLACE_CHILDREN }
}

/**
 * Account / settings — org-level admin + the signed-in user's own settings.
 * Lives as a drill-in section so the new sidebar replaces the old account
 * drawer (Venues/Team/.../Billing, then Profile/Notifications).
 */
/**
 * Account drill-in for the OLD nav. Hand-written here rather than mapped from
 * the new tree, which is why it needed gating separately — and why the
 * nav-gating invariant test now walks BOTH trees.
 *
 * The SECTION is deliberately ungated: Profile and Notifications are the
 * user's own settings and every role needs them. Only the org-administration
 * children carry `org.account`, so a non-admin drilling in sees exactly those
 * two entries.
 */
const ACCOUNT_SECTION: NavItem = {
  id: 'account',
  label: 'Account',
  icon: Settings,
  to: ({ orgId }) => `/${orgId}/account/organisation/venues`,
  children: [
    {
      id: 'account-venues',
      capability: 'org.account',
      label: 'Venues',
      icon: Home,
      to: ({ orgId }) => `/${orgId}/account/organisation/venues`,
    },
    {
      id: 'account-team',
      capability: 'org.account',
      label: 'Team',
      icon: Users,
      to: ({ orgId }) => `/${orgId}/account/organisation/team`,
    },
    {
      id: 'account-groups',
      capability: 'org.account',
      label: 'Groups',
      icon: Boxes,
      to: ({ orgId }) => `/${orgId}/account/organisation/groups`,
    },
    {
      id: 'account-settings',
      capability: 'org.account',
      label: 'Company Settings',
      icon: Building2,
      to: ({ orgId }) => `/${orgId}/account/organisation/settings`,
    },
    {
      id: 'account-tags',
      capability: 'org.account',
      label: 'Tags',
      icon: Tag,
      to: ({ orgId }) => `/${orgId}/account/organisation/tags`,
    },
    {
      id: 'account-brand',
      capability: 'org.account',
      label: 'Brand Kits',
      icon: Paintbrush,
      to: ({ orgId }) => `/${orgId}/account/organisation/brand`,
      // Stay active on the deep editor route (.../brand/:id).
      activeMatch: 'prefix',
    },
    {
      id: 'account-domains',
      capability: 'org.account',
      label: 'Domains',
      icon: AtSign,
      to: ({ orgId }) => `/${orgId}/marketing/email/domains`,
    },
    {
      id: 'account-event-log',
      capability: 'org.account',
      label: 'Event Log',
      icon: FileClock,
      to: ({ orgId }) => `/${orgId}/account/organisation/event-log`,
    },
    {
      id: 'account-billing',
      capability: 'org.account',
      label: 'Billing',
      icon: CreditCard,
      to: ({ orgId }) => `/${orgId}/account/organisation/billing`,
    },
    {
      id: 'account-profile',
      label: 'Profile',
      icon: User,
      separatorBefore: true,
      to: ({ orgId }) => `/${orgId}/account/profile`,
    },
    {
      id: 'account-notifications',
      label: 'Notifications',
      icon: Bell,
      to: ({ orgId }) => `/${orgId}/account/notifications`,
    },
  ],
}

/**
 * Support — the customer support agent surface.
 *
 * Hand-written here rather than mapped from the new tree, because the new nav
 * has no Support section yet: this is the old UI's main nav, which is where the
 * surface is being built. Sits directly above Account, the same slot the
 * Knowledge Base rows used to occupy.
 *
 * `nav.support` is open to everyone, and gates the routes as well
 * (`paths.tsx`), so the badge and the gate cannot disagree. The badge is a
 * label, not the gate.
 */
const SUPPORT_SECTION: NavItem = {
  id: 'support',
  capability: 'nav.support',
  label: 'Support',
  icon: LifeBuoy,
  to: ({ orgId }) => `/${orgId}/support`,
  children: [
    {
      id: 'support-home',
      label: 'Overview',
      icon: LifeBuoy,
      activeMatch: 'exact',
      to: ({ orgId }) => `/${orgId}/support`,
    },
    {
      id: 'support-cases',
      label: 'Conversations',
      icon: MessageSquare,
      // Stay active on a case's own chat route (.../cases/:case_id).
      activeMatch: 'prefix',
      to: ({ orgId }) => `/${orgId}/support/cases`,
    },
    {
      id: 'support-videos',
      label: 'Videos',
      icon: PlayCircle,
      separatorBefore: true,
      to: ({ orgId }) => `/${orgId}/support/videos`,
    },
  ],
}

/** Secondary ("Tools") group — old routes. */
const mappedOldSecondary: NavItem[] = orgNavSecondary
  .filter((item) => !OMIT_IDS.has(item.id))
  .map(toOldNavItem)
  .map(withMarketplaceChildren)

// Account belongs in the marketplace/scanner group (before the
// engineering-agents divider), not appended at the very end. Support sits
// directly above it.
const oldSecondaryEngIdx = mappedOldSecondary.findIndex(
  (item) => item.id === 'engineering-agents'
)
export const oldOrgNavSecondary: NavItem[] =
  oldSecondaryEngIdx === -1
    ? [...mappedOldSecondary, SUPPORT_SECTION, ACCOUNT_SECTION]
    : [
        ...mappedOldSecondary.slice(0, oldSecondaryEngIdx),
        SUPPORT_SECTION,
        ACCOUNT_SECTION,
        ...mappedOldSecondary.slice(oldSecondaryEngIdx),
      ]

/** Flat tree for path-matching helpers (breadcrumb / active section). */
export const oldOrgNav: NavItem[] = [...oldOrgNavPrimary, ...oldOrgNavSecondary]

/**
 * Ancestor chain (section → child) whose old-route href is the longest prefix
 * of `pathname`. Powers URL-driven drill auto-open and the top-bar breadcrumb.
 * Walks the OLD tree (the shared config helpers are hardcoded to the new tree).
 */
export function findOldNavChain(
  pathname: string,
  ctx: NavContext,
  tree: NavItem[] = oldOrgNav
): NavItem[] {
  const matches: { chain: NavItem[]; len: number }[] = []
  const consider = (item: NavItem, parents: NavItem[]) => {
    const chain = [...parents, item]
    const href = resolveHref(item, ctx)?.split('?')[0]
    if (href && (pathname === href || pathname.startsWith(`${href}/`))) {
      matches.push({ chain, len: href.length })
    }
    for (const child of item.children ?? []) consider(child, chain)
  }
  for (const section of tree) consider(section, [])
  if (matches.length === 0) return []
  return matches.reduce((a, b) => (b.len > a.len ? b : a)).chain
}
